import React from "react";

import { useTranslation } from "utils";

import { CarbonProgram } from "models";

import { Button, Card, Collapse, Flex, MainPage, StepItemCard, StickyFooterButtons } from "components";

interface FootprintingOnboardingStepsProps {
  onBegin: () => void;
  carbonProgram: CarbonProgram;
}

const FootprintingOnboardingSteps: React.FC<FootprintingOnboardingStepsProps> = ({ onBegin, carbonProgram }) => {
  const { t } = useTranslation(["footprinting"]);
  const showPayouts = carbonProgram.platformFeatures?.footprinting?.payouts ?? true;

  const steps = [
    {
      title: t("onboarding.step1Title"),
      description: t("onboarding.step1Description"),
    },
    {
      title: t("onboarding.step2Title"),
      description: t("onboarding.step2Description"),
    },
    {
      title: t("onboarding.step3Title"),
      description: t("onboarding.step3Description"),
    },
    {
      title: t("onboarding.step4Title"),
      description: t("onboarding.step4Description"),
    },
    ...(showPayouts
      ? [
          {
            title: t("onboarding.step5Title"),
            description: t("onboarding.step5Description"),
          },
        ]
      : []),
  ];

  const mainContent = (
    <div>
      <Flex>
        <h2>{t("onboarding.learnHowItWorksTitle")}</h2>
      </Flex>
      <Card className="margin-bottom-100">
        <Flex gap="1rem" vertical>
          <p className="paragraph-m">
            {t("onboarding.learnHowItWorksDescription", {
              payoutText: showPayouts ? t("onboarding.payoutText") : "",
            })}
          </p>
        </Flex>
      </Card>
      <Collapse
        items={[
          {
            key: "guide",
            label: (
              <Flex align="center">
                <h3 className="padding-left-100">{t("onboarding.stepHeadline")}</h3>
              </Flex>
            ),
            children: (
              <>
                {steps.map((step, idx) => (
                  <StepItemCard key={idx} stepNumber={idx + 1} title={step.title} description={step.description} />
                ))}
                <Card className="info-banner padding-100 margin-top-150">
                  <p className="paragraph-s-bold margin-bottom-0">{t("onboarding.needHelpHeadline")}</p>
                  <p className="paragraph-s grey-600-text">{t("onboarding.needHelpDescription")}</p>
                </Card>
              </>
            ),
          },
        ]}
        defaultActiveKey={["guide"]}
      />
      <Collapse
        items={[
          {
            key: "guide",
            label: (
              <Flex align="center">
                <h3 className="padding-left-100">{t("onboarding.howDoWeCalculateResults")}</h3>
              </Flex>
            ),
            children: (
              <>
                <p className="paragraph-m">{t("onboarding.howDoWeCalculateResultsDescription")}</p>
              </>
            ),
          },
        ]}
        defaultActiveKey={["guide"]}
      />
    </div>
  );

  return (
    <>
      <MainPage pageName="FootprintingOnboarding">{mainContent}</MainPage>
      <StickyFooterButtons absolute>
        <Button type="primary" size="large" block onClick={onBegin} data-testid="footprinting-onboarding-begin">
          {t("onboarding.letsBegin")}
        </Button>
      </StickyFooterButtons>
    </>
  );
};

export default FootprintingOnboardingSteps;
